import React, { useEffect } from 'react';
import styled from 'styled-components';

/**
 * The `InputGroup` is used to group checkboxes and radio buttons together within a fieldset
 * 
 * * The `direction` prop should let the the component user to choose the group display direction to be `row` or column
 * 
 * * The `className` prop should prop should apply classNames to the entire component
 * 
 * * The `groupRequired` prop passed into the rendered children is used to apply the required (asterisk) to the group header instead of the input itself 
 * * when required prop  is turned on
 * 
 */

export interface InputGroup {
  direction: 'column' | 'row';
  children?: React.ReactNode;
  disabled?: boolean;
  required?: boolean;
  name: string;
  headerText: string;
  className?: string;
}

export const InputGroup = ({
  direction,
  children,
  disabled,
  required,
  name,
  headerText,
  className,
}: InputGroup) => {
  const directionClass = direction === 'column' ? '' : 'flex-sm-row';

  const requiredClass = required && headerText ? 'required' : '';  

  const setCheckedState = () => {
    const groupInputs = document.querySelectorAll(
      `input[name=${name}]`
    ) as NodeListOf<HTMLInputElement>;
    if (required) {
      const checkedInputs = document.querySelectorAll(
        `input[name=${name}]:checked`
      ) as NodeListOf<HTMLInputElement>;

      if (checkedInputs.length === 0) {
        groupInputs.forEach(box => box.required = true);
      } else {
        groupInputs.forEach(box => box.required = false);
      }
    }
  };

  const renderChildren = () => {
    return React.Children.map<React.ReactNode, React.ReactNode>(
      children,
      function(child) {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, {
            disabled,
            name,
            required,
            onClick: setCheckedState(),
          });
        }
      }
    );
  };
  return (
    <InputGroupContainer className={className}>
      <h5 className={requiredClass}>{headerText}</h5>
      <section
        className={`d-flex flex-column ${directionClass} justify-content-between AD_InputGroup`}
      >
        {renderChildren()}
      </section>
    </InputGroupContainer>
  );
};

export default InputGroup;

const InputGroupContainer = styled.fieldset`
  .AD_InputGroup {
    &.flex-column.flex-sm-row > * {
      margin-right: 1rem;
      &:last-child {
        margin-right: 0;
      }
    }
    &.flex-row > * {
      margin-right: 1rem;
      &:last-child {
        margin-right: 0;
      }
    }
    &.flex-column > * {
      margin-bottom: 1rem;
      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  h5 {
    font-weight: bold;
    &.required {
      &:after {
        content: '*';
        margin-left: 0.15rem;
        display: inline-block;
        color: #db0020;
        color: var(--color-danger-500);
      }
    }
  }
  .AD_InputGroup {
    .bx--checkbox:required + .bx--checkbox-label .bx--checkbox-label-text::after,
    .bx--radio-button:required + .bx--radio-button__label span:nth-child(2)::after,
    .bx--toggle-input:required + .bx--toggle-input__label .bx--toggle__text--off::after,
    .bx--toggle-input:required + .bx--toggle-input__label .bx--toggle__text--on::after {
      content: '' !important;
    }
    .bx--form-item, .bx--radio-button-wrapper {
      position: relative;
    }
    input {
      bottom: 0 !important;
    }
  }
`;
